import { useState } from 'react';
import { Group, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { TemplateWithoutSections } from 'src/data-capture/hooks/templates/useTemplates';
import { Button } from 'src/design-system/Button';
import type { FamilyMeeting } from 'src/meetings/hooks/useScopedMeetings';
import { GenerateMeetingAgendaModal } from 'src/non-financial-data/v2/family/GenerateMeetingAgendaModal';
import { ExampleStack, type Example } from '../';

const TEMPLATE_NAMES = [
  'Annual review',
  'Quarterly review',
  'Initial discovery meeting',
  'Cashflow planning session',
  'Retirement options review',
  'Investment strategy review',
  'Protection and insurance review',
  'Estate planning and IHT discussion',
  'Mortgage and lending review',
  'Business owner planning meeting',
  'Pension consolidation review',
  'ISA and general investment account review',
  'Vulnerable client check-in',
  'Family wealth planning meeting',
  'Ongoing service annual suitability assessment for accumulating clients',
];

function template(displayName: string, index: number): TemplateWithoutSections {
  return {
    id: `template-${index}`,
    displayName,
    description: '',
    supportsMulticlient: false,
    eligibleForSummarisation: true,
    templateType: 'MEETING_AGENDA',
    isActive: true,
    isDefault: index === 0,
    markdownImageCollectionId: null,
  };
}

const templates = TEMPLATE_NAMES.map(template);

const FAMILY_ID = 'sandbox-family';
const ADVISER = { id: 'sandbox-user', firstName: 'Ada', lastName: 'Lovelace' };
const CLIENTS = [
  { id: 'sandbox-client-1', firstName: 'Priya', lastName: 'Shah' },
  { id: 'sandbox-client-2', firstName: 'Sam', lastName: 'Shah' },
];

const meeting = {
  id: 'sandbox-event',
  eventParticipants: CLIENTS.map(client => ({
    id: `event-participant-${client.id}`,
    rsvp: 'ACCEPTED',
    participant: {
      id: `participant-${client.id}`,
      participantType: 'CLIENT',
      name: null,
      description: null,
      emailAddress: null,
      client,
      user: null,
      createdAt: '2026-09-30T09:00:00Z',
    },
  })),
} as unknown as FamilyMeeting;

/**
 * The modal resolves its attendees through the real hooks, which would otherwise hit the
 * API with these made-up ids and read the tenant's actual families. Seeding every query
 * they touch into a sandbox-only client keeps the demo offline; the default query function
 * throws, so anything left unseeded settles as a failed query instead of falling through
 * to the network.
 */
function createSandboxQueryClient(): QueryClient {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: false,
        staleTime: Infinity,
        queryFn: ({ queryKey }) => {
          throw new Error(`Unseeded sandbox query ${JSON.stringify(queryKey)}`);
        },
      },
    },
  });
  queryClient.setQueryData(['me'], ADVISER);
  queryClient.setQueryData(['family-roles'], []);
  // useMeetingFamilyId lists the families before its family-scope early return, so the
  // unfiltered list is fetched even though the sandbox already knows the family id.
  queryClient.setQueryData(['families', {}], []);
  queryClient.setQueryData(['families', FAMILY_ID], {
    id: FAMILY_ID,
    name: 'Shah',
    mainFamilyMember: { clientId: CLIENTS[0].id, familyRoleId: null },
    secondaryFamilyMembers: [],
    members: CLIENTS.slice(1).map(client => ({ clientId: client.id, familyRoleId: null })),
  });
  queryClient.setQueryData(['clients', 'all-pages', { familyId: FAMILY_ID }], CLIENTS);
  for (const client of CLIENTS) {
    queryClient.setQueryData(['clients', client.id, 'non-financial-data'], { entities: {} });
  }
  return queryClient;
}

function ModalLauncher({ templates }: { templates: TemplateWithoutSections[] }) {
  const [opened, { open, close }] = useDisclosure(false);
  const [generatedWith, setGeneratedWith] = useState<string>();
  const [queryClient] = useState(createSandboxQueryClient);

  return (
    <Group>
      <Button onClick={open}>Open the agenda modal</Button>
      {generatedWith && (
        <Text size="sm" c="dimmed">
          Generated with “{generatedWith}”
        </Text>
      )}
      <QueryClientProvider client={queryClient}>
        <GenerateMeetingAgendaModal
          opened={opened}
          onClose={close}
          scope={{ kind: 'family', familyId: FAMILY_ID }}
          meeting={meeting}
          templates={templates}
          isGenerating={false}
          onConfirm={async templateId => {
            setGeneratedWith(templates.find(t => t.id === templateId)?.displayName);
            close();
          }}
        />
      </QueryClientProvider>
    </Group>
  );
}

function longList(): Example {
  return {
    description:
      'Fifteen agenda templates: the dropdown should scroll inside a capped height and open with a focused search box (ENG-1567)',
    render: <ModalLauncher templates={templates} />,
  };
}

function shortList(): Example {
  return {
    description: 'Three templates: below the search threshold, so no search box appears',
    render: <ModalLauncher templates={templates.slice(0, 3)} />,
  };
}

function noTemplates(): Example {
  return {
    description: 'No agenda templates at all: the dropdown is replaced by a prompt to create one',
    render: <ModalLauncher templates={[]} />,
  };
}

export default function MeetingAgendaTemplateSandbox() {
  return <ExampleStack examples={[longList(), shortList(), noTemplates()]} />;
}
